import React, { useEffect, useState, useRef, useMemo } from "react";
import API from "common-ui-components/components/utils/API";
import responseHandler from "app/manage/software-repository/utils/responseHandler";
import { useNavigate } from "react-router-dom";
import { navigateToManualDeployment } from "../../utils/vulnerabilityUtils";
import { unstable_batchedUpdates } from "react-dom";
import styles from "./VulnerabilityExplorer.module.css";
import Button from "common-ui-components/components/button";

const SEV_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };
const SEV_ROLE = {
  Critical: "danger",
  High: "warning",
  Medium: "accent",
  Low: "success",
};
const PAGE_SIZE = 30;
const SOFTWARE_PAGE_SIZE = 30;
const VERSION_PAGE_SIZE = 20;
const PRELOAD_OFFSET = 180;
const CHIP_PRELOAD_OFFSET = 120;

const SkeletonRow = () => (
  <div className={["shimmer", styles.skeletonRow].join(" ")} />
);

const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Reusable faceted software -> version -> CVE explorer.
 *
 * @param softwareSummaryUrl   endpoint returning [{ softwareId, softwareName, cveCount }]
 * @param versionSummaryUrl    fn(softwareId) => endpoint returning [{ softwareVersionId, versionName, cveCount }]
 * @param cveDetailsUrl        fn(softwareVersionId) => endpoint returning CVEs (paginated via &page=&size=)
 * @param extraFilters         filter array merged into every request body (e.g. device/policy scoping)
 * @param onPatchNow           optional override; defaults to navigateToManualDeployment
 * @param onCveClick           optional override for CVE-id click
 * @param height               px/rem height of the whole pane
 */
const VulnerabilityExplorer = ({
  softwareSummaryUrl,
  versionSummaryUrl,
  cveDetailsUrl,
  extraFilters = [],
  onPatchNow,
  onCveClick,
  height = "35rem",
}) => {
  const navigate = useNavigate();

  const [softwareList, setSoftwareList] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [softwarePagination, setSoftwarePagination] = useState({
    page: 0,
    hasMore: true,
    isFetchingMore: false,
  });
  const searchTimeoutRef = useRef(null);

  const [currentSoftwareId, setCurrentSoftwareId] = useState(null);
  const [versionsBySoftware, setVersionsBySoftware] = useState({}); // { [softwareId]: { isLoading, isFetchingMore, page, hasMore, versions: [] } }
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [isSwitchingSoftware, setIsSwitchingSoftware] = useState(false);
  const [chipScrollState, setChipScrollState] = useState({
    canLeft: false,
    canRight: false,
  });

  // cveCache[versionId] = { isLoading, isFetchingMore, page, hasMore, cves: [] }
  const [cveCache, setCveCache] = useState({});
  const softwareListRef = useRef(null);
  const detailScrollRef = useRef(null);
  const chipRowRef = useRef(null);

  // ── Fetch software list (search-aware, paginated) ────────────────────
  const fetchSoftwareList = async (pageNum, query, isAppend) => {
    try {
      unstable_batchedUpdates(() => {
        if (isAppend) {
          setSoftwarePagination((prev) => ({ ...prev, isFetchingMore: true }));
        } else {
          setIsLoadingList(true);
          if (query) setIsSearching(true);
        }
      });

      const response = await API.post(
        `${softwareSummaryUrl}?page=${pageNum}&size=${SOFTWARE_PAGE_SIZE}`,
        {
          search: query ? [{ column: "softwareName", searchKey: query }] : [],
          sort: {},
          filter: extraFilters,
        },
      );

      const formatted = responseHandler(response);
      if (!formatted.ok) throw formatted;

      const mapped = (formatted.body?.result || []).map((s) => ({
        id: s.softwareId,
        label: s.softwareName,
        count: s.cveCount || 0,
      }));

      setSoftwareList((prev) => (isAppend ? [...prev, ...mapped] : mapped));
      setSoftwarePagination({
        page: pageNum,
        hasMore: mapped.length >= SOFTWARE_PAGE_SIZE,
        isFetchingMore: false,
      });

      if (!isAppend) {
        setCurrentSoftwareId((prev) => prev ?? mapped[0]?.id ?? null);
      }
    } catch (e) {
      console.error(e);
      setSoftwarePagination((prev) => ({
        ...prev,
        isFetchingMore: false,
        hasMore: false,
      }));
    } finally {
      setIsLoadingList(false);
      setIsSearching(false);
    }
  };

  useEffect(() => {
    fetchSoftwareList(0, searchQuery, false);
  }, [searchQuery, softwareSummaryUrl]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setSearchQuery(value), 500);
  };

  const handleSidebarScroll = () => {
    const el = softwareListRef.current;
    if (!el) return;

    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < PRELOAD_OFFSET;

    if (
      !nearBottom ||
      !softwarePagination.hasMore ||
      softwarePagination.isFetchingMore ||
      isLoadingList
    ) {
      return;
    }

    fetchSoftwareList(softwarePagination.page + 1, searchQuery, true);
  };

  // ── Fetch versions for the selected software (cached + paginated) ────
  const fetchVersionsForSoftware = async (softwareId, pageNum, isAppend) => {
    setVersionsBySoftware((prev) => ({
      ...prev,
      [softwareId]: {
        ...(prev[softwareId] || { versions: [], hasMore: true }),
        isLoading: isAppend ? prev[softwareId]?.isLoading : true,
        isFetchingMore: isAppend,
      },
    }));
    if (!isAppend) setIsSwitchingSoftware(true);

    try {
      const response = await API.post(
        `${versionSummaryUrl(softwareId)}?page=${pageNum}&size=${VERSION_PAGE_SIZE}`,
        { search: [], sort: {}, filter: extraFilters },
      );
      const formatted = responseHandler(response);
      if (!formatted.ok) throw formatted;

      const newVersions = (formatted.body?.result || []).map((v) => ({
        id: v.softwareVersionId,
        label: v.versionName,
        count: v.cveCount || 0,
      }));

      setVersionsBySoftware((prev) => ({
        ...prev,
        [softwareId]: {
          isLoading: false,
          isFetchingMore: false,
          page: pageNum,
          hasMore: newVersions.length >= VERSION_PAGE_SIZE,
          versions:
            pageNum === 0
              ? newVersions
              : [...(prev[softwareId]?.versions || []), ...newVersions],
        },
      }));

      if (pageNum === 0) {
        setCurrentVersionId((prev) => prev ?? newVersions[0]?.id ?? null);
      }
    } catch (e) {
      console.error(e);
      setVersionsBySoftware((prev) => ({
        ...prev,
        [softwareId]: {
          ...(prev[softwareId] || { versions: [] }),
          isLoading: false,
          isFetchingMore: false,
          hasMore: false,
        },
      }));
    } finally {
      setIsSwitchingSoftware(false);
    }
  };

  useEffect(() => {
    if (!currentSoftwareId) return;

    if (versionsBySoftware[currentSoftwareId]) {
      const firstVersion =
        versionsBySoftware[currentSoftwareId].versions?.[0]?.id;
      setCurrentVersionId((prev) => prev ?? firstVersion ?? null);
      setIsSwitchingSoftware(false);
      return;
    }

    fetchVersionsForSoftware(currentSoftwareId, 0, false);
  }, [currentSoftwareId]);

  const currentVersions = versionsBySoftware[currentSoftwareId]?.versions || [];
  const versionsLoading =
    versionsBySoftware[currentSoftwareId]?.isLoading || false;

  // ── Chip row arrow nav + preload-on-scroll ────────────────────────────
  const updateChipScrollState = () => {
    const el = chipRowRef.current;
    if (!el) {
      setChipScrollState({ canLeft: false, canRight: false });
      return;
    }
    setChipScrollState({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  };

  useEffect(() => {
    updateChipScrollState();
  }, [
    currentVersions.length,
    currentVersionId,
    isSwitchingSoftware,
    versionsLoading,
  ]);

  const handleChipScroll = () => {
    updateChipScrollState();

    const el = chipRowRef.current;
    if (!el || !currentSoftwareId) return;

    const cache = versionsBySoftware[currentSoftwareId];
    if (!cache) return;

    const nearEnd =
      el.scrollWidth - el.scrollLeft - el.clientWidth < CHIP_PRELOAD_OFFSET;

    if (!nearEnd || !cache.hasMore || cache.isFetchingMore || cache.isLoading)
      return;

    fetchVersionsForSoftware(currentSoftwareId, (cache.page ?? 0) + 1, true);
  };

  const scrollChipRow = (direction) => {
    const el = chipRowRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 160, behavior: "smooth" });
  };

  // ── Fetch CVEs for a single version (paginated, cached) ──────────────
  const fetchCvesForVersion = async (versionId, pageNum = 0) => {
    setCveCache((prev) => ({
      ...prev,
      [versionId]: {
        ...(prev[versionId] || { cves: [], hasMore: true }),
        isLoading: pageNum === 0 ? true : prev[versionId]?.isLoading,
        isFetchingMore: pageNum > 0,
      },
    }));

    try {
      const response = await API.post(
        `${cveDetailsUrl(versionId)}&page=${pageNum}&size=${PAGE_SIZE}`,
        { search: [], sort: {}, filter: extraFilters },
      );
      const formatted = responseHandler(response);
      if (!formatted.ok) throw formatted;

      const newCves = formatted.body?.result || [];

      setCveCache((prev) => ({
        ...prev,
        [versionId]: {
          isLoading: false,
          isFetchingMore: false,
          page: pageNum,
          hasMore: newCves.length >= PAGE_SIZE,
          cves:
            pageNum === 0
              ? newCves
              : [...(prev[versionId]?.cves || []), ...newCves],
        },
      }));
    } catch (e) {
      console.error(e);
      setCveCache((prev) => ({
        ...prev,
        [versionId]: {
          ...(prev[versionId] || { cves: [] }),
          isLoading: false,
          isFetchingMore: false,
          hasMore: false,
        },
      }));
    }
  };

  // Fetch CVEs whenever the selected version changes.
  useEffect(() => {
    if (!currentSoftwareId) return;

    if (currentVersionId) {
      if (!cveCache[currentVersionId]) fetchCvesForVersion(currentVersionId, 0);
      return;
    }
  }, [currentVersionId, currentSoftwareId]);

  const handleDetailScroll = () => {
    const detail = detailScrollRef.current;
    if (!detail || !currentVersionId) return;

    const cache = cveCache[currentVersionId];
    if (!cache) return;

    const hasRoomToPreload =
      detail.scrollHeight - detail.scrollTop - detail.clientHeight <
      PRELOAD_OFFSET;

    if (
      !hasRoomToPreload ||
      !cache.hasMore ||
      cache.isFetchingMore ||
      cache.isLoading
    ) {
      return;
    }

    fetchCvesForVersion(currentVersionId, (cache.page ?? 0) + 1);
  };

  // ── Derived CVE entries for the right pane ───────────────────────────
  const entries = useMemo(() => {
    if (!currentVersionId) {
      return [];
    }

    const cache = cveCache[currentVersionId];
    return (cache?.cves || []).map((c) => ({
      ...c,
      versionLabel: currentVersions.find((v) => v.id === currentVersionId)
        ?.label,
    }));
  }, [currentVersionId, cveCache, currentVersions]);

  const anyLoading = currentVersionId
    ? cveCache[currentVersionId]?.isLoading
    : false;

  const isFetchingMore = currentVersionId
    ? cveCache[currentVersionId]?.isFetchingMore
    : false;

  const currentSoftware = softwareList.find((s) => s.id === currentSoftwareId);

  const handlePatchNow = (cve) =>
    onPatchNow
      ? onPatchNow(cve)
      : navigateToManualDeployment(navigate, [{ orgPatchId: cve?.orgPatchId }]);

  const handleCveClick = (cve) =>
    onCveClick
      ? onCveClick(cve)
      : navigate(
          { pathname: `cve-details`, search: `?cveId=${cve.cveId}` },
          { state: { orgVulnerabilityId: cve.orgVulnerabilityId } },
        );

  return (
    <div className={styles.wrapper} style={{ height }}>
      {/* Software sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.searchWrap}>
          <input
            className={styles.searchInput}
            placeholder="Search software"
            onChange={handleSearchInput}
          />
          {isSearching && <span className={styles.searchSpinner} />}
        </div>

        <div
          className={styles.softwareList}
          ref={softwareListRef}
          onScroll={handleSidebarScroll}
        >
          {isLoadingList
            ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            : softwareList.map((s) => (
                <div
                  key={s.id}
                  className={[
                    styles.softwareRow,
                    s.id === currentSoftwareId ? styles.softwareRowActive : "",
                  ].join(" ")}
                  onClick={() => {
                    if (s.id === currentSoftwareId) return;
                    setCurrentSoftwareId(s.id);
                    setCurrentVersionId(null);
                    setIsSwitchingSoftware(true);
                  }}
                >
                  <span className={styles.softwareLabel}>{s.label}</span>
                  {s.count > 0 && (
                    <span
                      className={
                        s.id == currentSoftwareId
                          ? styles.selectedCount
                          : styles.countBadge
                      }
                    >
                      {s.count}
                    </span>
                  )}
                </div>
              ))}
          {!isLoadingList && softwareList.length === 0 && (
            <div className={styles.emptyText}>No software found.</div>
          )}
          {!isLoadingList && softwarePagination.isFetchingMore && (
            <SkeletonRow />
          )}
        </div>
      </div>

      {/* Detail pane */}
      <div
        className={styles.detail}
        ref={detailScrollRef}
        onScroll={handleDetailScroll}
      >
        {isSwitchingSoftware && (
          <div className={styles.centerSpinner}>
            <span
              className={styles.spinner}
              aria-label="Loading software versions"
            />
          </div>
        )}

        {!isSwitchingSoftware && currentSoftware && (
          <>
            <div className={styles.detailHeader}>
              <span className={styles.detailTitle}>
                {currentSoftware.label}
              </span>
              <span className={styles.detailSubtitle}>
                {currentVersions.length} version
                {currentVersions.length === 1 ? "" : "s"} installed
              </span>
            </div>

            {!versionsLoading && !isSwitchingSoftware && (
              <div className={styles.chipRowWrapper}>
                <div
                  className={styles.chipRow}
                  ref={chipRowRef}
                  onScroll={handleChipScroll}
                >
                  {currentVersions.map((v) => (
                    <span
                      key={v.id}
                      className={[
                        styles.chip,
                        currentVersionId === v.id ? styles.chipActive : "",
                      ].join(" ")}
                      onClick={() => {
                        setCurrentVersionId(v.id);
                        if (!cveCache[v.id]) fetchCvesForVersion(v.id, 0);
                      }}
                    >
                      v{v.label}
                      {v.count > 0 && (
                        <span className={styles.chipCount}>{v.count}</span>
                      )}
                    </span>
                  ))}
                  {versionsBySoftware[currentSoftwareId]?.isFetchingMore && (
                    <span
                      className={["shimmer", styles.chipSkeleton].join(" ")}
                    />
                  )}
                </div>

                <button
                  type="button"
                  className={styles.chipNavBtn}
                  onClick={() => scrollChipRow(-1)}
                  disabled={!chipScrollState.canLeft}
                  aria-label="Scroll versions left"
                >
                  <ChevronLeft />
                </button>
                <button
                  type="button"
                  className={styles.chipNavBtn}
                  onClick={() => scrollChipRow(1)}
                  disabled={!chipScrollState.canRight}
                  aria-label="Scroll versions right"
                >
                  <ChevronRight />
                </button>
              </div>
            )}

            {versionsLoading && (
              <div className={styles.chipRow}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <span
                    key={i}
                    className={["shimmer", styles.chipSkeleton].join(" ")}
                  />
                ))}
              </div>
            )}

            <div className={styles.cveList}>
              {anyLoading && entries.length === 0 ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="shimmer"
                    style={{
                      height: "60px",
                      borderRadius: "6px",
                      marginBottom: "8px",
                    }}
                  />
                ))
              ) : entries.length === 0 ? (
                <div className={styles.emptyText}>
                  No known vulnerabilities for this selection.
                </div>
              ) : (
                (() => {
                  return entries.map((c) => {
                    const role = SEV_ROLE[c.severity] || "accent";
                    return (
                      <div
                        key={c.cveId + c.versionLabel}
                        className={styles.cveCard}
                      >
                        <div className={styles.cveInfo}>
                          <div className={styles.cveTopRow}>
                            <span
                              className={styles.cveId}
                              onClick={() => handleCveClick(c)}
                            >
                              {c.cveId}
                            </span>
                            <span
                              className={[
                                styles.pill,
                                styles[
                                  `sevPill${(c.severity || "").charAt(0).toUpperCase()}${(c.severity || "").slice(1).toLowerCase()}`
                                ] || "",
                              ].join(" ")}
                            >
                              {c.severity || "--"}
                            </span>
                            <span
                              className={[
                                styles.pill,
                                styles.affectedPill,
                              ].join(" ")}
                            >
                              {c.deviceCount ?? c.affectedSystems ?? 0}{" "}
                              {(c.deviceCount ?? c.affectedSystems ?? 0) === 1
                                ? "Affected System"
                                : "Affected Systems"}
                            </span>
                          </div>
                          <div className={styles.cveMeta}>
                            Fixed in {c.fixedIn || c.fixedVersion || "—"} ·
                            Published {c.publishedDate || "--"}
                          </div>
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() => handlePatchNow(c)}
                        >
                          Patch now
                        </Button>
                      </div>
                    );
                  });
                })()
              )}
              {isFetchingMore && (
                <div className={styles.loadMoreContainer}>
                  <span className={styles.loadMoreSpinner} />
                  <span className={styles.loadMoreText}>Loading more…</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VulnerabilityExplorer;
